import {
  Component,
  Injectable,
  OnInit,
  HostListener,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
  viewChild,
} from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import * as _ from 'underscore';

import { AppSettings, Settings } from 'app/app.settings';
import { Enums, IEnums } from 'app/app.enums';
import { rotate } from './utils/app-animation';
import { MenuService } from './menu/menu.service';
import { SystemService, ConfigService } from '@core/services';
import * as Models from '@shared/models';
import { SidenavComponent } from './sidenav/sidenav.component';
import { UserMenuComponent } from './user-menu/user-menu.component';

@Injectable()
@Component({
  selector: 'app-concierge-services',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  animations: [rotate],
  providers: [MenuService],
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    SidenavComponent,
    UserMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, AfterViewInit {
  private readonly menuService = inject(MenuService);
  private readonly systemService = inject(SystemService);
  private readonly configService = inject(ConfigService);
  readonly appSettings = inject(AppSettings);
  readonly router = inject(Router);

  readonly sidenav = viewChild<{ toggle: () => void }>('sidenav');
  readonly options = viewChild.required<{ toggle: () => void }>('options');
  readonly backToTop = viewChild.required<ElementRef>('backToTop');
  readonly searchBar = viewChild<ElementRef>('searchBar');

  // Computed signals from config - no manual change detection needed!
  readonly client = computed(() => this.configService.config()?.client ?? '');
  readonly app = computed(() => this.configService.config()?.appName ?? '');
  readonly logo = computed(() => this.configService.config()?.headerLogo ?? '');
  readonly appName = computed(() => `${this.client()} ${this.app()}`);
  readonly appLogo = computed(() => this.logo());

  currentYear = 0;
  enums: IEnums = Enums;
  menus = ['vertical', 'horizontal'];
  menuOption = '';
  menuTypes = ['default', 'compact', 'mini'];
  menuTypeOption = '';
  isMobile = false;
  searchTerm = '';
  settings: Settings;
  showSidenav = false;
  showInfoContent = false;
  toggleSearchBar = false;
  translationInfo!: Models.ITranslationInfo;

  private defaultMenu = ''; // declared for return default menu when window resized

  private searchDebounced = _.debounce(() => {
    this.systemService.search(this.searchTerm);
  }, 300);

  sidenavCollapsed = false;

  constructor() {
    this.settings = this.appSettings.settings;

    // Effect to handle menu type changes from SystemService signal
    effect(() => {
      const menuType = this.systemService.menuType();
      if (menuType) {
        this.options()?.toggle();
        setTimeout(() => {
          this.options()?.toggle();
        }, 10);
        this.settings.menuType = menuType;
      }
    });
  }

  ngOnInit(): void {
    this.menuOption = this.settings.menu;
    this.menuTypeOption = this.settings.menuType;
    this.defaultMenu = this.settings.menu;
    this.currentYear = new Date().getFullYear();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.settings.loadingSpinner = false;
    }, 300);
  }

  getTranslation(_key: string): string {
    const decoded = '';
    // if (this.translationInfo.languageId !== null) {
    //     decoded = this.languageService.getTranslationByKey(key, this.translationInfo.languageId);
    // }
    return decoded;
  }
  search(event: KeyboardEvent): void {
    if (event.keyCode === 27) {
      this.searchTerm = '';
    }
    this.searchDebounced();
  }

  toggleSidenav(): void {
    this.sidenav()?.toggle();
  }

  chooseMenu(): void {
    this.settings.menu = this.menuOption;
    this.defaultMenu = this.menuOption;
    if (this.menuOption === 'horizontal') {
      this.settings.fixedSidenav = false;
    }
    this.router.navigate(['/']);
  }

  chooseMenuType(): void {
    this.settings.menuType = this.menuTypeOption;
  }

  closeInfoContent(showInfoContent: boolean): void {
    this.showInfoContent = !showInfoContent;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth <= 960) {
      this.settings.sidenavIsOpened = false;
      this.settings.sidenavIsPinned = false;
      this.settings.menu = 'vertical';
    } else {
      if (this.defaultMenu === 'horizontal') {
        this.settings.menu = 'horizontal';
      } else {
        this.settings.menu = 'vertical';
      }
      this.settings.sidenavIsOpened = true;
      this.settings.sidenavIsPinned = true;
    }
  }

  onPsScrollY(event: Event): void {
    const target = event.target as HTMLElement;
    const backToTopEl = this.backToTop();
    if (target?.scrollTop > 200) {
      backToTopEl.nativeElement.style.display = 'flex';
    } else {
      backToTopEl.nativeElement.style.display = 'none';
    }
  }

  scrollToTop(): void {
    // this.pss.forEach(ps => {
    //     if (ps.elementRef.nativeElement.id == 'main') {
    //         ps.scrollToTop(0, 250);
    //         this.backToTop.nativeElement.style.display = 'none';
    //     }
    // });
  }

  showSearch(): void {
    this.toggleSearchBar = !this.toggleSearchBar;
    this.searchBar()?.nativeElement.focus();
  }

  closeSubMenus(): void {
    if (this.settings.menu === 'vertical') {
      this.menuService.closeAllSubMenus();
    }
  }

  private updateLanguages(): void {
    //this.translationInfo = this.languageService.getTranslationInfo();
  }

  onSidenavCollapse(collapsed: boolean): void {
    this.sidenavCollapsed = collapsed;
    // Add a small delay to allow the animation to complete
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }
}
